/**
 * Pinned from `/Users/saher/code/cafe/Tarabeeza-Backend` (read-only source), branch
 * at the time of writing:
 *   - src/modules/call-center/call-center-customers.controller.ts + .service.ts
 *   - src/modules/call-center/call-center-orders.controller.ts + .service.ts
 *   - src/modules/call-center/dto/*.ts
 *   - docs/ADR/ADR-0021-call-center.md
 * One file, so a later change on that side is a one-file fix here.
 *
 * `Customer`, `CustomerDetail` and `OrderHistoryEntry` are NOT redeclared
 * here — `GET /call-center/customers/search` answers the same `Customer` row
 * `CustomersService.findAll` always has (`@/Pages/CRM/lib`'s `CustomerRow`),
 * and `GET /customers/:id` (unchanged by this ADR) is what "repeat" reads
 * from. Reusing the existing CRM pin keeps one shape for one resource.
 *
 * ## Two decisions every screen in this module has to keep visible
 *
 * 1. **A call-centre order is NOT guest-placed.** ADR-0021 §2.2:
 *    `heldForWaiterAt` exists for a second pair of eyes on a GUEST'S OWN
 *    unread basket. A call-centre agent reads the order back to the caller
 *    before it exists — they already ARE that second pair of eyes — so
 *    `CallCenterOrdersService.create` always passes `placedByGuest: false`.
 *    The order fires straight to CONFIRMED → kitchen. **No screen in this
 *    module may show a "held for waiter" / "awaiting confirmation" state —
 *    it cannot occur on a call-centre order, and drawing it implies a wait
 *    that will never end.**
 * 2. **Phone lookup is a PII search**, not the generic customer search.
 *    `customers.search_phone` (separate from `customers.view`), throttled to
 *    20/min server-side (`@Throttle`), and NOT `@Audit`-marked — the handler
 *    writes its own audit row with a masked query (`***1234`) and a result
 *    COUNT, never the matched names/phones/emails. There is no bulk-lookup
 *    affordance here on purpose: one phone number in, one throttled request.
 *    There is also no returning-customer IDENTITY: the agent is the
 *    authenticated principal, looking up a CRM record about someone else.
 *    `findOrCreateByPhone` is a CRM write, never a sign-in — no screen here
 *    may imply a customer "logs in" or "is recognized" the way a person is.
 */
import type { FulfillmentType, PaymentMethod } from '@/lib/shared-types';
import type { OrderItemRow, OrderRow } from '@/Redux/Slices/operationsApi';

// -------------------------------------------------------------------- errors

export function apiError(error: unknown, fallback: string): string {
  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(message)) return message.filter(Boolean).join(' · ');
  return (typeof message === 'string' && message.trim()) || fallback;
}

export function apiStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}

// -------------------------------------------------------- PII phone lookup

/**
 * `GET /call-center/customers/search?phone=` — deliberately a different
 * shape call from the generic customer search: `phone` is required and
 * floor-length (3), matching `SearchCustomersByPhoneDto`. This is the ONLY
 * place in this module that may call that route; nothing else in the app
 * should reuse it as a general search box.
 */
export interface SearchCustomersByPhoneParams {
  phone: string;
  page?: number;
  limit?: number;
}

// -------------------------------------------------------------- addresses

/** ADR-0021 §2.4 — deliberately narrow: no zone, no fee, no serviceability check. */
export interface CustomerAddress {
  id: string;
  tenantId: string;
  customerId: string;
  label: string | null;
  line1: string;
  line2: string | null;
  area: string | null;
  city: string | null;
  landmark: string | null;
  lat: string | null;
  lng: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomerAddressDto {
  label?: string;
  line1: string;
  line2?: string;
  area?: string;
  city?: string;
  landmark?: string;
  lat?: number;
  lng?: number;
  isDefault?: boolean;
}

export type UpdateCustomerAddressDto = Partial<CreateCustomerAddressDto>;

// ------------------------------------------------------------------ orders

/**
 * `Order` columns ADR-0021 adds, on top of the existing `OrderRow` pin
 * (`@/Redux/Slices/operationsApi`) — never redeclared, extended.
 */
export interface CallCenterOrder extends OrderRow {
  channel: 'POS' | 'GUEST_QR' | 'CALL_CENTER';
  deliveryAddressId: string | null;
  deliveryAddressText: string | null;
  tenderPreference: PaymentMethod | null;
  /** Echoed back by `CallCenterOrdersService.create` alongside the order. */
  customerId?: string;
}

/**
 * `fulfillmentType` excludes DINE_IN at the type level — a caller is never
 * seated at a table, and the desk exists for exactly the other two.
 */
export interface CreateCallCenterOrderDto {
  customerId?: string;
  customerPhone?: string;
  customerName?: string;
  branchId: string;
  fulfillmentType: Extract<FulfillmentType, 'TAKEAWAY' | 'DELIVERY'>;
  deliveryAddressId?: string;
  scheduledFor?: string;
  /** The caller's DECLARED INTENT, never a captured payment — ADR-0021 §2.8. */
  tenderPreference?: PaymentMethod;
  notes?: string;
  items: CallCenterOrderLine[];
  submit?: boolean;
  operationId?: string;
}

export interface CallCenterOrderLine {
  itemId: string;
  variantId?: string;
  quantity?: number;
  notes?: string;
  modifiers?: { modifierId: string; quantity?: number }[];
}

/**
 * ADR-0021 §2.8 — a fixed, tenant-wide allow-list, not a configuration
 * surface. Choosing anything else needs `orders.override_payment_policy`,
 * which the base `CALL_CENTER_AGENT` role does NOT hold — this UI mirrors
 * that by disabling the option rather than letting the request 403.
 */
export const CALL_CENTER_ALLOWED_TENDERS: PaymentMethod[] = ['CASH', 'CARD'];

export interface TransferOrderBranchDto {
  branchId: string;
}

/**
 * `GET /call-center/orders/:id/repeat` — an UNSAVED scaffold. It creates
 * nothing; the agent reviews it with the caller and submits it through the
 * ordinary create endpoint, which re-resolves price/availability for real.
 */
export interface RepeatOrderDraft {
  branchId: string;
  fulfillmentType: string;
  items: CallCenterOrderLine[];
}

/** For rendering a past order's lines when building the repeat draft — the caller reviews names, not just ids. */
export type { OrderItemRow };
